import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";
import { FormikProps } from "formik";

import { formatEuro } from "../../../../../../../format/formatNumber";
import { BalanceEntryFormValues, ClaimFormValues } from "../../../ClaimFormFields.types";
import { createEmptyBalanceEntry } from "../../../ClaimFormFields.utils";

export function useBalanceHistory(formik: FormikProps<ClaimFormValues>) {
  const { t, i18n } = useTranslation();
  const entries = formik.values.balanceEntries;

  const computeSummary = (): ReactNode => {
    // Only a dated entry can be the latest one, so an undated list has a row count but no balance.
    const datedEntries = entries.filter(
      (e): e is BalanceEntryFormValues & { date: Date } => e.date instanceof Date && !isNaN(e.date.getTime())
    );
    if (!datedEntries.length) {
      return t("claims.balanceHistory.summaryCount", { count: entries.length });
    }
    // `>=` so that among rows sharing the newest date the last one wins, which is the entry the
    // Verbindlichkeiten list shows as the balance: saved rows arrive ordered by date and then by when
    // they were recorded, and a row just added sits at the end. With `>` the first of a same-dated
    // group would win here and the two screens would disagree.
    const latest = datedEntries.reduce((prev, curr) => (curr.date >= prev.date ? curr : prev));
    // An entry whose amount (Betrag) is still empty has an unknown balance, not a balance of zero.
    const amount = latest.amount !== null ? formatEuro(latest.amount, i18n.language) : "–";
    const date = format(latest.date, "dd.MM.yyyy");
    return (
      <>
        {t("claims.balanceHistory.summaryBefore", { count: entries.length })} <strong>{amount}</strong>{" "}
        {t("claims.balanceHistory.summaryAfter", { date })}
      </>
    );
  };

  const addEntry = () => {
    void formik.setFieldValue("balanceEntries", [...entries, createEmptyBalanceEntry()]);
  };

  const removeEntry = (index: number) => {
    void formik.setFieldValue(
      "balanceEntries",
      entries.filter((_, i) => i !== index)
    );
  };

  return { entries, computeSummary, addEntry, removeEntry };
}
